import React, { useState, useEffect, useRef } from 'react';
import { ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import useApi from 'hooks/useApi';
import {
  setCurrentNotification,
  setShowTypeNotifications,
} from 'redux/actions/notificationActions';
import { ContactsModal } from 'shared/components/SharedStyles/SharedStyles';
import { setNewCommunication, selectCurrentClient } from 'redux/actions/clientActions';
import { colors } from 'shared/defaultTheme';
import NotificationForm from 'shared/components/NotificationForm';
import uuid from 'react-uuid';
import ContactNotificationForm from 'shared/components/ContactNotificationForm';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';

import { useHistory } from 'react-router-dom';
import useNotification from 'hooks/useNotification';
import ShareNotifyContacts from './ShareNotifyContacts';

const AddNotify = ({
  onCancel,
  isDetailCommunication,
  isNotifyClient,
  notifyClientId,
  isClientsSelectorDisabled,
  onCreate,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const { cusNavId, clientId, isCustomer, mail } = useSelector((state) => state.auth);
  const notificationState = useSelector((state) => state.notification);
  const documentState = useSelector((state) => state.document);
  const [selectedClient, setSelectedClient] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [dataForm, setDataForm] = useState(null);
  const [clients, setClients] = useState([]);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [currentClientId, setNewClient] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [disableButton, setDisabledButton] = useState(false);
  const newClient = useRef(null);

  const { handleSelectNotificationReview, getIndividualById } = useNotification();

  const [, getClientsData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/clients/${cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: loadingCreate }, createNotification] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [, getContactsData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const clientsData = async () => {
    const filters = [];
    if (clientId || notifyClientId) {
      filters.push({
        model: 'Client',
        field: 'ClientId',
        filter: [clientId || notifyClientId],
      });
    }
    const {
      data: { data },
    } = await getClientsData({
      data: {
        filters,
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    });
    if (notificationState?.currentNotification && !isDetailCommunication) {
      setSelectedClient(
        data.find((client) => client.clientId === notificationState?.currentNotification.clientId)
          .clientName
      );
    }
    if (notificationState?.currentNotification && isDetailCommunication && !isCustomer) {
      const newData = data.find((client) => client.clientId === clientId).clientName;
      setSelectedClient(newData);
    }
    setClients(data);
  };

  useEffect(() => {
    clientsData();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
      if (!isDetailCommunication) dispatch(setCurrentNotification(null));
    };
  }, []);

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  useEffect(() => {
    if (currentClientId) {
      const getContactList = async () => {
        const {
          data: { data },
        } = await getContactsData({
          url: `${window.API_V1}/cli/contacts/${cusNavId}/${currentClientId || clientId}`,
        });

        const filterData = data.filter((x) => x.mail);
        setContacts(filterData);
      };
      getContactList();
    }
    if (isDetailCommunication) {
      const getContactList = async () => {
        const {
          data: { data },
        } = await getContactsData({
          url: `${window.API_V1}/cli/contacts/${cusNavId}/${currentClientId || clientId}`,
        });

        const filterData = data.filter((x) => x.mail);
        setContacts(filterData);
      };
      getContactList();
    }
  }, [currentClientId, isDetailCommunication]);

  const getPermissionsFromContacts = (documentContacts) => {
    return documentContacts.map((contactEmail) => {
      return {
        u: contactEmail,
        p: 'r-',
      };
    });
  };

  const buildDocumentsObject = (documentDetails, documentContacts) => {
    if (!documentDetails) return [];
    const { id, name, pathId, size } = documentDetails;
    const permissions = getPermissionsFromContacts(documentContacts);
    return [{ id, name, pathId, size, permissions }];
  };

  const buildAnswersObject = (documentDetails) => {
    if (!documentDetails) return [];
    return [
      {
        id: uuid(),
        text: t('document_shared_file'),
        createdBy: mail,
        idsDocs: [documentDetails.id],
      },
    ];
  };

  const onCreateNotification = async (values) => {
    setDataForm(values);
    const newClientsId = values.clientsId.map((dataClientId) => {
      const findClients = clients.find((client) => client.clientId === dataClientId);
      return { ...findClients, isActiveAll: true };
    });
    setSelectedClients(newClientsId);
    setOpenContactsModal(true);
  };

  const onCreateContactNotification = async (values) => {
    try {
      setDisabledButton(true);
      const newContacts = values.linkToContact.map((contactMail) => {
        const findContact = contacts.find((x) => x.mail === contactMail);
        return { name: findContact.nameComplete, mail: findContact.mail, active: true };
      });
      const newData = [
        {
          createdBy: values.responsibles[0],
          name: values.name,
          date: moment(new Date()).toISOString(),
          linkToContact: newContacts,
          responsibles: values.responsibles,
          description: values.description,
          answers: buildAnswersObject(documentState?.documentDtails),
          docs: buildDocumentsObject(documentState?.documentDtails, values.linkToContact),
        },
      ];
      const {
        data: { data },
      } = await createNotification({
        url: `${window.API_V1}/cli/notifications/${cusNavId}/${
          clientId || notifyClientId
        }/${encodeURIComponent(mail)}`,
        data: newData,
      });
      dispatch(selectCurrentClient(selectedClients[0]));
      dispatch(setShowTypeNotifications(false));
      const currentNotification = await getIndividualById({
        notificationId: data[0],
        currentClientId: clientId || notifyClientId,
      });
      handleSelectNotificationReview(currentNotification);
      history.push(`/notificacion_details`, { isClientNotify: false });
      if (!data) {
        throw new Error(
          data.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      console.error(error);
      setShowError(true);
      if (error.response?.data.errors && error.response?.data.errors[0].detail === 'CCNO-16') {
        setErrorMessage(t('general_error_notification'));
      } else {
        setErrorMessage(t('custom_field_operation_error_message'));
      }
      setDisabledButton(false);
    }
  };

  const onSubmit = async (values) => {
    onCreateNotification(values);
  };

  const onContactSubmit = async (values) => {
    onCreateContactNotification(values);
  };

  const handleCancel = () => {
    if (!isDetailCommunication) dispatch(setCurrentNotification(null));
    dispatch(setNewCommunication(undefined));
    onCancel();
  };

  const onCloseContactsModal = () => {
    setOpenContactsModal(false);
  };

  return (
    <>
      <div className="mr-2">
        {isCustomer && !isNotifyClient ? (
          <NotificationForm
            onCancel={handleCancel}
            onSubmit={onSubmit}
            onToggleError={onToggleError}
            showError={showError}
            clients={clients}
            clientId={clientId}
            isDetailCommunication={isDetailCommunication}
          />
        ) : (
          <ContactNotificationForm
            errorMessage={errorMessage}
            onCancel={handleCancel}
            onSubmit={onContactSubmit}
            onToggleError={onToggleError}
            showError={showError}
            clients={clients}
            contacts={contacts}
            newClient={newClient}
            setNewClient={setNewClient}
            loadingCreate={loadingCreate}
            selectedClient={selectedClient}
            disableButton={disableButton}
            isDetailCommunication={isDetailCommunication}
            isClientsSelectorDisabled={isClientsSelectorDisabled}
          />
        )}
      </div>
      <ContactsModal isOpen={openContactsModal} toggle={onCloseContactsModal} size="lg" top="1%">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseContactsModal}
        />
        <ModalBody>
          <ShareNotifyContacts
            dataForm={dataForm}
            selectedClients={selectedClients}
            setSelectedClients={setSelectedClients}
            handleClose={() => {
              setOpenContactsModal(false);
              onCancel();
            }}
            onBack={onCloseContactsModal}
            onCreate={onCreate}
          />
        </ModalBody>
      </ContactsModal>
    </>
  );
};

AddNotify.defaultProps = {
  isDetailCommunication: false,
  isNotifyClient: false,
  isClientsSelectorDisabled: false,
  onCreate: () => {},
};

AddNotify.propTypes = {
  onCancel: PropTypes.func.isRequired,
  isDetailCommunication: PropTypes.bool,
  isNotifyClient: PropTypes.bool,
  notifyClientId: PropTypes.string.isRequired,
  isClientsSelectorDisabled: PropTypes.bool,
  onCreate: PropTypes.func,
};

export default AddNotify;
